import { useEffect, useRef, useState } from "react";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { useKeyDown } from "@react-hooks-library/core";
import { AxiosError } from "axios";
import { Controller, useForm } from "react-hook-form";

import { AgentVersionName } from "src/agents/AgentVersionName";
import {
  VERSION_DESCRIPTION_MAX_LENGTH,
  VERSION_NAME_MAX_LENGTH,
  VERSION_NAME_MIN_LENGTH,
} from "src/agents/constants";
import {
  buildDefaultAgentVersionConfiguration,
  useAgentVersions,
  useCreateAgentVersion,
} from "src/agents/queries/management";
import { AgentVersion, AgentVersionConfigurationInput } from "src/agents/types";
import { validateAgentVersionConfigurationJson } from "src/agents/validation";
import { Button } from "src/base-components/Button";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { ErrorHint } from "src/base-components/ErrorHint";
import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import { Select } from "src/base-components/Select";
import { Textarea } from "src/base-components/Textarea";
import { Icon } from "src/design-system/Icon";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { usePowertools } from "src/powertools/hooks";
import { readTextFromClipboard } from "src/utils/clipboard";
import { logger } from "src/utils/logger";

type CreateAgentVersionModalProps = {
  open: boolean;
  onClose: () => void;
  agentId: string;
  versionId?: string;
  startFromScratch?: boolean;
  onCreateSuccess?: (agentId: string, newVersion: AgentVersion) => void;
};

type CreateAgentVersionForm = {
  agentId: string;
  versionId: string;
  versionName: string;
  description: string;
};

const START_FROM_SCRATCH_KEY = "scratch";
const PASTE_FROM_JSON_KEY = "paste-json";

export const CreateAgentVersionModal: React.FC<
  CreateAgentVersionModalProps
> = ({
  open,
  onClose: onCloseProp,
  agentId,
  versionId,
  startFromScratch = false,
  onCreateSuccess,
}) => {
  const { data: versions, isLoading } = useAgentVersions(agentId);
  const { mutateAsync: createVersion } = useCreateAgentVersion();
  const isPowerToolsEnabled = usePowertools();
  const formRef = useRef<HTMLFormElement>(null);
  const [jsonInput, setJsonInput] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const getDefaultVersionId = () => {
    if (versionId) return versionId;
    if (startFromScratch) return START_FROM_SCRATCH_KEY;
    return versions?.[0]?.id ?? START_FROM_SCRATCH_KEY;
  };

  const form = useForm<CreateAgentVersionForm>({
    mode: "onChange",
    values: {
      agentId,
      versionId: getDefaultVersionId(),
      versionName: "",
      description: "",
    },
  });

  useEffect(() => {
    if (!open || !isPowerToolsEnabled || versionId) return;

    let cancelled = false;

    const detectClipboardConfig = async () => {
      const text = await readTextFromClipboard();
      if (cancelled || !text) return;
      try {
        validateAgentVersionConfigurationJson(text);
        setJsonInput(text);
        // Set only versionId via setValue to avoid resetting other fields
        // (using the values prop would re-apply versionName/description too)
        form.setValue("versionId", PASTE_FROM_JSON_KEY);
        toastActions.default({
          title: "Configuration detected in clipboard",
          description:
            "We found a valid Agent configuration in your clipboard and pre-filled it for you.",
        });
      } catch {
        // Clipboard content is not a valid agent config — ignore
      }
    };

    void detectClipboardConfig();

    return () => {
      cancelled = true;
    };
  }, [open, isPowerToolsEnabled, versionId, form]);

  const onClose = () => {
    form.reset();
    setJsonInput("");
    setJsonError(null);
    onCloseProp();
  };

  // Not taking advantage of the native cmd+enter form functionality
  // because it doesn't work with textarea (which we use for the description)
  useKeyDown(
    ["Enter"],
    (e) => {
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        formRef.current?.requestSubmit();
      }
    },
    { target: formRef },
  );

  const onSubmit = async (data: CreateAgentVersionForm) => {
    try {
      const isFromScratch = data.versionId === START_FROM_SCRATCH_KEY;
      const isFromJson = data.versionId === PASTE_FROM_JSON_KEY;

      let configuration: AgentVersionConfigurationInput | undefined;
      if (isFromJson) {
        try {
          configuration = validateAgentVersionConfigurationJson(jsonInput);
        } catch (err) {
          setJsonError(
            err instanceof Error ? err.message : "Invalid configuration",
          );
          return;
        }
      } else if (isFromScratch) {
        configuration = buildDefaultAgentVersionConfiguration();
      }

      const newVersion = await createVersion({
        agentId: data.agentId,
        versionName: data.versionName,
        description: data.description,
        sourceVersionId:
          isFromScratch || isFromJson ? undefined : data.versionId,
        configuration: isFromScratch || isFromJson ? configuration : undefined,
      });

      toastActions.success({
        title: "Version created",
        description: `Successfully created ${data.versionName}`,
      });

      if (onCreateSuccess) {
        onCreateSuccess(data.agentId, newVersion);
      }

      onClose();
    } catch (error) {
      logger.error("Failed to create version", error);
      const errorMessage =
        error instanceof AxiosError && error.status === 409
          ? "Version name already exists"
          : "An unknown error occurred";
      toastActions.failure({
        title: "Failed to create version",
        description: errorMessage,
      });
    }
  };

  const selectedVersionId = form.watch("versionId");

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header>Create version</Modal.Header>
      <form ref={formRef} onSubmit={form.handleSubmit(onSubmit)}>
        <Modal.Content>
          <div className="flex flex-col gap-5">
            <div className="flex flex-col">
              <Label mb="mb-0.5" required>
                Create from
              </Label>
              <span className="mb-2 font-inter-normal-12px text-gray-500">
                Select a version to duplicate
              </span>
              <Controller
                control={form.control}
                name="versionId"
                render={({ field, fieldState }) => {
                  const versionOptions =
                    versions?.map((version) => ({
                      key: version.id,
                      value: (
                        <AgentVersionName
                          name={version.name}
                          status={version.status}
                        />
                      ),
                    })) ?? [];

                  const allOptions = [
                    {
                      key: START_FROM_SCRATCH_KEY,
                      value: <span>Start from scratch</span>,
                    },
                    ...(isPowerToolsEnabled
                      ? [
                          {
                            key: PASTE_FROM_JSON_KEY,
                            value: <span>Import from JSON</span>,
                          },
                        ]
                      : []),
                    ...versionOptions,
                  ];

                  return (
                    <>
                      <Select
                        dataLoc="create-agent-version-select"
                        errored={!!fieldState.error}
                        options={allOptions}
                        value={field.value}
                        onChange={field.onChange}
                      />
                      {fieldState.error?.message && (
                        <ErrorHint>{fieldState.error.message}</ErrorHint>
                      )}
                    </>
                  );
                }}
                rules={{
                  required: "Please select a version to create from",
                }}
              />
              {selectedVersionId === PASTE_FROM_JSON_KEY && (
                <div className="mt-3">
                  <div className="h-48 overflow-auto rounded-lg border border-gray-200">
                    <CodeEditor
                      backgroundColor="white"
                      basicSetup={{
                        lineNumbers: true,
                        highlightActiveLine: true,
                        autocompletion: false,
                      }}
                      dataLoc="paste-config-json-editor"
                      language="json"
                      placeholder="Paste Agent version configuration JSON here..."
                      value={jsonInput}
                      lineWrapping
                      onChange={(value) => {
                        setJsonInput(value ?? "");
                        setJsonError(null);
                      }}
                    />
                  </div>
                  {jsonError && <ErrorHint>{jsonError}</ErrorHint>}
                </div>
              )}
            </div>
            <div>
              <Label required>Version name</Label>
              <Input
                errored={!!form.formState.errors.versionName}
                fullWidth
                {...form.register("versionName", {
                  required: "Version name is required",
                  minLength: {
                    value: VERSION_NAME_MIN_LENGTH,
                    message: `Version name must be at least ${VERSION_NAME_MIN_LENGTH} character`,
                  },
                  maxLength: {
                    value: VERSION_NAME_MAX_LENGTH,
                    message: `Version name must be less than ${VERSION_NAME_MAX_LENGTH} characters`,
                  },
                })}
                placeholder="Type version name"
              />
              {form.formState.errors.versionName && (
                <ErrorHint>
                  {form.formState.errors.versionName?.message}
                </ErrorHint>
              )}
            </div>

            <div>
              <div className="mb-2 flex items-center gap-1">
                <Label mb="mb-0">Description</Label>
                <Tooltip
                  body="Adding a description helps others understand what this version does and how it differs from other versions."
                  placement="right-start"
                >
                  <Icon icon={faInfoCircle} size="xs" />
                </Tooltip>
              </div>
              <Textarea
                errored={!!form.formState.errors.description}
                placeholder="Type version description"
                fullWidth
                {...form.register("description", {
                  maxLength: {
                    value: VERSION_DESCRIPTION_MAX_LENGTH,
                    message: `Description must be less than ${VERSION_DESCRIPTION_MAX_LENGTH} characters`,
                  },
                })}
              />
              {form.formState.errors.description && (
                <ErrorHint>
                  {form.formState.errors.description.message}
                </ErrorHint>
              )}
            </div>
          </div>
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              disabled={
                isLoading ||
                form.formState.isSubmitting ||
                !form.formState.isValid ||
                (selectedVersionId === PASTE_FROM_JSON_KEY && !jsonInput.trim())
              }
              htmlType="submit"
              loading={isLoading || form.formState.isSubmitting}
              variant="primary"
            >
              Create
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
